import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/default-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import { useFormContext } from "react-hook-form";
import { FilterState, normalizeFilterInput } from "../(context)/filter";
import { ROUTES_CONFIG } from "@/config/routes.config";
import { useNavigate } from "react-router-dom";

type Props = {
  onRefetchReady?: (fn: () => void) => void;
};

const OverviewTable = ({ onRefetchReady }: Props) => {
  const navigate = useNavigate();
  const { trpc } = useTRPC();
  const { watch, getValues, setValue } = useFormContext<FilterState>();
  const filterState = watch();

  const query = useQuery({
    queryKey: ["clubs", filterState],
    queryFn: async () => {
      const input = normalizeFilterInput(getValues());
      const clubs = await trpc.clubs.search.query(input);
      return clubs;
    },
    enabled: false,
  });

  React.useEffect(() => {
    onRefetchReady?.(() => query.refetch());
  }, [query.refetch]);

  React.useEffect(() => {
    query.refetch();
  }, [filterState.page]);

  return (
    <div className="h-full overflow-auto">
      <div className="flex flex-col justify-between gap-4 h-full">
        <div className="bg-foreground/10 rounded-md border pb-2 pr-2 pl-2 overflow-auto h-full">
          <Table className='overflowclip w-full relative' divClassname='overflow-y-scroll max-h-[100%]'>
          {query.isFetching && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/60">
                <Icons.Loader2 className="w-4 h-4 animate-spin" />
            </div>
            )}
            <TableHeader className="sticky top-0 w-full border-b bg-sidebar/50 backdrop-blur-md z-10">
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead className="w-[300px]">Spelers</TableHead>
                <TableHead className="w-[300px]">Type</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query?.data?.clubs?.map((club) => (
                <TableRow
                  key={club.id}
                  className="hover:bg-foreground/10 group/row"
                >
                  <TableCell 
                    className="group/name cursor-pointer"
                    onClick={()=>{
                      if(club?.tm_id === undefined) return;
                      navigate(ROUTES_CONFIG.APP.CLUB_DETAIL.$buildPath({ params: { club_id: club?.id } }))
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div className="size-8 aspect-square rounded-full bg-foreground/10">
                        <Avatar>
                          <AvatarImage
                            src={club?.imageUrl ?? undefined}
                            alt={club?.name ?? undefined}
                          />
                          <AvatarFallback>
                            {club?.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <p className="group-hover/name:underline font-medium">{club?.name || "-"}</p>
                    </div>
                  </TableCell>
                  <TableCell>{club.playerCount || "-"}</TableCell>
                  <TableCell>{club.type || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-foreground/50">
            {query?.data?.total} rijen gevonden
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => setValue("page", 1)}
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                setValue("page", Math.max(1, Number(filterState.page) - 1))
              }
              disabled={parseInt(filterState?.page?.toString() || "0") <= 1}
            >
              <Icons.ChevronLeft className="w-4 h-4" />
            </Button>
            <Input 
              value={filterState.page}
              onChange={(e) => {
                setValue('page', e.currentTarget.valueAsNumber);
              }}
              onBlur={() => {
                query.refetch();
              }}
              type="number"
              min={1}
              max={query?.data?.pages || 1}
            />
            <Button
              variant="outline"
              onClick={() =>
                setValue(
                  "page",
                  Math.min(
                    Number(query?.data?.pages || 1),
                    Number(filterState.page) + 1
                  )
                )
              }
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setValue("page", query?.data?.pages || 1)}
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTable;
